// @ts-strict-ignore
import type { WorkflowState } from 'flow/store/redux/state';
import type { DerivedNodeConnectorState } from 'flow/store/redux/types';
import { buildEdges, buildSkeletonEdge, replaceNodesInEdges } from './edges';
import type { WorkflowNode } from './nodes';
import {
  buildNodeFolder,
  buildNodes,
  buildSkeletonNode,
  isNodeFolderStateCollapsed,
  workflowNodeType,
} from './nodes';
import type { Graph } from './types';

export function buildGraph(
  actions: WorkflowState['workflowData']['actions'],
  dependencies: WorkflowState['workflowData']['dependencies'],
  workflowProps?: WorkflowState['workflowProps'],
): Graph {
  const nodes = buildNodes(actions, workflowProps);
  const edges = buildEdges(dependencies, nodes);

  const graph: Graph = {
    nodes,
    edges,
  };

  return graph;
}

/**
 * Builds and returns a new graph where node folders are populated into the graph
 * Adjusts edges to point to the node folders where necessary.
 */
export function organizeNodeFolders(
  graph: Graph,
  nodeFolderState: WorkflowState['ui']['nodeFolders']['inputAttribute'],
): Graph {
  const folderNode = buildNodeFolder({
    childType: workflowNodeType.inputAttribute,
    children: [],
  });
  const isCollapsed = isNodeFolderStateCollapsed(nodeFolderState);

  // memoize edge adjustments as we traverse nodes
  const childToFolderIdMap: Record<WorkflowNode['id'], WorkflowNode['id']> = {};

  // rebuild graph where collapsed child nodes are removed from the top level
  // of the graph and are nested in the folder's children
  const nodes: WorkflowNode[] = graph.nodes.reduce((builtNodes, curNode) => {
    if (
      curNode.type === workflowNodeType.inputAttribute
      && isCollapsed
    ) {
      folderNode.data.children.push(curNode);
      // save for edge adjustment
      childToFolderIdMap[curNode.id] = folderNode.id;
      return builtNodes;
    }
    return [...builtNodes, curNode];
  }, []);

  // add nodeFolder to graph
  nodes.push(folderNode);

  const edges = isCollapsed
    ? replaceNodesInEdges({
      edges: graph.edges, prevIdToNewIdMap: childToFolderIdMap,
    })
    : graph.edges;

  return { nodes, edges };
}

export function appendAdditionalGraphElements(
  graph: Graph,
  uiStatePartial: {
    ownerNodeId: WorkflowState['ui']['connector']['ownerNodeId'];
  },
): Graph {
  const { ownerNodeId } = uiStatePartial;
  const skeletonNode = buildSkeletonNode();
  const skeletonEdge = buildSkeletonEdge(ownerNodeId);

  graph.nodes.push(skeletonNode);
  graph.edges.push(skeletonEdge);

  return graph;
}

export function shouldAddSkeletonNodeAndEdge(
  nodeConnectorState: DerivedNodeConnectorState,
  isEditingLinkedChildAction: boolean,
): boolean {
  const { intent, interactionState } = nodeConnectorState;
  return intent === 'add'
    && (interactionState === 'hovered') && !isEditingLinkedChildAction;
}

